import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '@/utils/api-request';
import { IndianRupee, Plus, Trash2, Copy, ChevronDown, ChevronRight, Save } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';

interface FeeEntry {
    id?: number;
    fee_type: string;
    frequency: string;
    refundable: string;
    amount: string;
}

interface ClassFees {
    className: string;
    fees: FeeEntry[];
    isExpanded: boolean;
}

interface FeeRange {
    id: number;
    curriculum: string | null;
    grade_from: string;
    grade_to: string;
    classes: string[];
}

interface Options {
    feeTypes: string[];
    frequencies: string[];
    refundableOptions: string[];
}

const FeeStructure: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [feeRanges, setFeeRanges] = useState<FeeRange[]>([]);
    const [options, setOptions] = useState<Options>({
        feeTypes: [],
        frequencies: [],
        refundableOptions: []
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [activeCurriculum, setActiveCurriculum] = useState<string | null>(null);
    const [classFees, setClassFees] = useState<Record<string, ClassFees[]>>({});
    // Calculate academic year dynamically (October to September cycle)
    // Oct 2025 - Sep 2026 = AY 2026-2027
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // 1-12
    const academicYear = currentMonth >= 10
        ? `${currentYear + 1}-${currentYear + 2}`
        : `${currentYear}-${currentYear + 1}`;

    // Fetch fee ranges and existing fee structure
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);

                // Fetch fee ranges
                const rangesResponse = await apiRequest(`/fee-structure/ranges/${id}`);
                if (rangesResponse.success) {
                    const ranges = rangesResponse.data.feeRanges || [];
                    setFeeRanges(ranges);
                    setOptions(rangesResponse.data.options);

                    // Set first curriculum as active
                    if (ranges.length > 0) {
                        const firstCurriculum = ranges[0].curriculum || 'default';
                        setActiveCurriculum(firstCurriculum);
                    }

                    // Initialize class fees structure
                    const initialClassFees: Record<string, ClassFees[]> = {};
                    ranges.forEach((range: FeeRange) => {
                        const currKey = range.curriculum || 'default';
                        // Several ranges can share a curriculum — merge, don't overwrite
                        const existing = initialClassFees[currKey] || [];
                        range.classes.forEach(cls => {
                            if (!existing.some(c => c.className === cls)) {
                                existing.push({ className: cls, fees: [], isExpanded: false });
                            }
                        });
                        initialClassFees[currKey] = existing;
                    });

                    // Fetch existing fee structure
                    const structureResponse = await apiRequest(`/fee-structure/${id}?academic_year=${academicYear}`);
                    if (structureResponse.success) {
                        const existingFees = structureResponse.data.feeStructure || [];

                        // Check if we have any classes from ranges
                        const hasClasses = Object.values(initialClassFees).some(classes => classes.length > 0);

                        // If no classes from ranges but we have fee data, build class structure from fees
                        if (!hasClasses && existingFees.length > 0) {
                            const classesFromFees = new Set<string>();
                            existingFees.forEach((fee: any) => classesFromFees.add(fee.class));

                            // Sort classes in logical order
                            const classOrder = ['Nursery', 'LKG', 'UKG', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];
                            const sortedClasses = Array.from(classesFromFees).sort((a, b) => {
                                const aIdx = classOrder.indexOf(a);
                                const bIdx = classOrder.indexOf(b);
                                if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
                                if (aIdx === -1) return 1;
                                if (bIdx === -1) return -1;
                                return aIdx - bIdx;
                            });

                            initialClassFees['default'] = sortedClasses.map(cls => ({
                                className: cls,
                                fees: [],
                                isExpanded: false
                            }));
                            setActiveCurriculum('default');
                        }

                        // Populate existing fees
                        // Get all curriculum keys from initialClassFees
                        const curriculumKeys = Object.keys(initialClassFees);

                        existingFees.forEach((fee: any) => {
                            let currKey = fee.curriculum || 'default';

                            // If curriculum key doesn't exist, try to find a matching curriculum
                            // or use the first available curriculum (for fees with null curriculum)
                            if (!initialClassFees[currKey]) {
                                // If fee has no curriculum but we have curricula from ranges, use the first one
                                if (!fee.curriculum && curriculumKeys.length > 0) {
                                    currKey = curriculumKeys[0];
                                }
                            }

                            if (initialClassFees[currKey]) {
                                const classItem = initialClassFees[currKey].find(c => c.className === fee.class);
                                if (classItem) {
                                    classItem.fees.push({
                                        id: fee.id,
                                        fee_type: fee.fee_type,
                                        frequency: fee.frequency,
                                        refundable: fee.refundable,
                                        amount: fee.amount.toString()
                                    });
                                    classItem.isExpanded = true;
                                }
                            }
                        });
                    }

                    setClassFees(initialClassFees);
                }
            } catch (error) {
                console.error('Error fetching fee data:', error);
                showToast('Failed to fetch fee data', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [id]);

    // Toggle class expansion
    const toggleClassExpansion = (curriculum: string, classIndex: number) => {
        setClassFees(prev => ({
            ...prev,
            [curriculum]: prev[curriculum].map((cls, idx) =>
                idx === classIndex ? { ...cls, isExpanded: !cls.isExpanded } : cls
            )
        }));
    };

    // Add fee row to a class
    const addFeeRow = (curriculum: string, classIndex: number) => {
        setClassFees(prev => ({
            ...prev,
            [curriculum]: prev[curriculum].map((cls, idx) =>
                idx === classIndex
                    ? {
                        ...cls,
                        fees: [...cls.fees, { fee_type: '', frequency: '', refundable: 'No', amount: '' }],
                        isExpanded: true
                    }
                    : cls
            )
        }));
    };

    // Remove fee row from a class
    const removeFeeRow = (curriculum: string, classIndex: number, feeIndex: number) => {
        setClassFees(prev => ({
            ...prev,
            [curriculum]: prev[curriculum].map((cls, idx) =>
                idx === classIndex
                    ? { ...cls, fees: cls.fees.filter((_, fIdx) => fIdx !== feeIndex) }
                    : cls
            )
        }));
    };

    // Update fee row
    const updateFeeRow = (curriculum: string, classIndex: number, feeIndex: number, field: keyof FeeEntry, value: string) => {
        setClassFees(prev => ({
            ...prev,
            [curriculum]: prev[curriculum].map((cls, idx) =>
                idx === classIndex
                    ? {
                        ...cls,
                        fees: cls.fees.map((fee, fIdx) =>
                            fIdx === feeIndex ? { ...fee, [field]: value } : fee
                        )
                    }
                    : cls
            )
        }));
    };

    // Apply fees to all classes in curriculum
    const applyToAllClasses = async (curriculum: string, sourceClassIndex: number) => {
        const sourceClass = classFees[curriculum][sourceClassIndex];
        if (!sourceClass.fees.length) {
            showToast('No fees to apply', 'error');
            return;
        }

        const targetClasses = classFees[curriculum]
            .filter((_, idx) => idx !== sourceClassIndex)
            .map(cls => cls.className);

        if (!targetClasses.length) {
            showToast('No other classes to apply to', 'error');
            return;
        }

        try {
            setIsSaving(true);
            const response = await apiRequest(`/fee-structure/${id}/apply-all`, {
                method: 'POST',
                body: JSON.stringify({
                    curriculum: curriculum === 'default' ? null : curriculum,
                    sourceClass: sourceClass.className,
                    targetClasses,
                    fees: sourceClass.fees.map(f => ({
                        fee_type: f.fee_type,
                        frequency: f.frequency,
                        refundable: f.refundable,
                        amount: f.amount
                    })),
                    academic_year: academicYear
                })
            });

            if (response.success) {
                showToast('Fees applied to all classes', 'success');

                // Update local state
                setClassFees(prev => ({
                    ...prev,
                    [curriculum]: prev[curriculum].map((cls, idx) =>
                        idx === sourceClassIndex
                            ? cls
                            : {
                                ...cls,
                                fees: sourceClass.fees.map(f => ({ ...f, id: undefined })),
                                isExpanded: true
                            }
                    )
                }));
            } else {
                showToast('Failed to apply fees', 'error');
            }
        } catch (error) {
            console.error('Error applying fees:', error);
            showToast('Failed to apply fees', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // Save all fees for current curriculum
    const saveAllFees = async () => {
        if (!activeCurriculum) return;

        const allFees: any[] = [];
        classFees[activeCurriculum].forEach(cls => {
            cls.fees.forEach(fee => {
                if (fee.fee_type && fee.amount) {
                    allFees.push({
                        class: cls.className,
                        fee_type: fee.fee_type,
                        frequency: fee.frequency,
                        refundable: fee.refundable,
                        amount: fee.amount
                    });
                }
            });
        });

        try {
            setIsSaving(true);
            const response = await apiRequest(`/fee-structure/${id}`, {
                method: 'POST',
                body: JSON.stringify({
                    curriculum: activeCurriculum === 'default' ? null : activeCurriculum,
                    fees: allFees,
                    academic_year: academicYear
                })
            });

            if (response.success) {
                showToast('Fee structure saved successfully', 'success');
            } else {
                showToast('Failed to save fee structure', 'error');
            }
        } catch (error) {
            console.error('Error saving fees:', error);
            showToast('Failed to save fee structure', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">Loading fee structure...</p>
            </div>
        );
    }

    // Show empty state only if no fee ranges AND no existing fee data
    const hasExistingFees = Object.values(classFees).some(classes => classes.some(c => c.fees.length > 0));

    if (feeRanges.length === 0 && !hasExistingFees) {
        return (
            <div className="h-full flex flex-col bg-gray-50">
                <div className="bg-white border-b border-gray-200 px-6 py-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-brandColor rounded-lg text-white">
                            <IndianRupee className="h-5 w-5" />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900">Fee Structure</h1>
                            <p className="text-xs text-gray-500">Manage fee structure for academic year {academicYear}</p>
                        </div>
                    </div>
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <p className="text-gray-500 mb-2">No fee ranges configured for this school.</p>
                        <p className="text-sm text-gray-400">Please configure fee ranges in the Admissions section first.</p>
                    </div>
                </div>
            </div>
        );
    }

    const curriculumTabs = feeRanges.map(r => r.curriculum || 'default');
    const uniqueTabs = [...new Set(curriculumTabs)];

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-brandColor rounded-lg text-white">
                            <IndianRupee className="h-5 w-5" />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900">Fee Structure</h1>
                            <p className="text-xs text-gray-500">Manage fee structure for academic year {academicYear}</p>
                        </div>
                    </div>
                    <Button
                        variant="primary"
                        onClick={saveAllFees}
                        disabled={isSaving}
                        leftIcon={<Save size={16} />}
                    >
                        {isSaving ? 'Saving...' : 'Save All'}
                    </Button>
                </div>
            </div>

            {/* Curriculum Tabs */}
            {uniqueTabs.length > 1 && (
                <div className="bg-white border-b border-gray-200 px-6">
                    <div className="flex gap-1">
                        {uniqueTabs.map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveCurriculum(tab)}
                                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                    activeCurriculum === tab
                                        ? 'border-brandColor text-brandColor'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {tab === 'default' ? 'General' : tab}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
                {activeCurriculum && classFees[activeCurriculum] && (
                    <div className="space-y-4">
                        {classFees[activeCurriculum].map((classItem, classIndex) => (
                            <div key={classItem.className} className="bg-white rounded-lg border border-gray-200">
                                {/* Class Header */}
                                <div
                                    className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
                                    onClick={() => toggleClassExpansion(activeCurriculum, classIndex)}
                                >
                                    <div className="flex items-center gap-3">
                                        {classItem.isExpanded ? (
                                            <ChevronDown size={18} className="text-gray-400" />
                                        ) : (
                                            <ChevronRight size={18} className="text-gray-400" />
                                        )}
                                        <span className="font-medium text-gray-900">{classItem.className}</span>
                                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                            {classItem.fees.length} fee{classItem.fees.length !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => addFeeRow(activeCurriculum, classIndex)}
                                            leftIcon={<Plus size={14} />}
                                        >
                                            Add Fee
                                        </Button>
                                        {classItem.fees.length > 0 && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => applyToAllClasses(activeCurriculum, classIndex)}
                                                disabled={isSaving}
                                                leftIcon={<Copy size={14} />}
                                            >
                                                Apply to All
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* Fees Table */}
                                {classItem.isExpanded && classItem.fees.length > 0 && (
                                    <div className="border-t border-gray-200">
                                        <table className="w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fee Type</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Refundable</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount (₹)</th>
                                                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-20">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {classItem.fees.map((fee, feeIndex) => (
                                                    <tr key={feeIndex}>
                                                        <td className="px-4 py-2">
                                                            <select
                                                                value={fee.fee_type}
                                                                onChange={(e) => updateFeeRow(activeCurriculum, classIndex, feeIndex, 'fee_type', e.target.value)}
                                                                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            >
                                                                <option value="">Select Type</option>
                                                                {options.feeTypes.map(type => (
                                                                    <option key={type} value={type}>{type}</option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            <select
                                                                value={fee.frequency}
                                                                onChange={(e) => updateFeeRow(activeCurriculum, classIndex, feeIndex, 'frequency', e.target.value)}
                                                                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            >
                                                                <option value="">Select Frequency</option>
                                                                {options.frequencies.map(freq => (
                                                                    <option key={freq} value={freq}>{freq}</option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            <select
                                                                value={fee.refundable}
                                                                onChange={(e) => updateFeeRow(activeCurriculum, classIndex, feeIndex, 'refundable', e.target.value)}
                                                                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            >
                                                                {options.refundableOptions.map(opt => (
                                                                    <option key={opt} value={opt}>{opt}</option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            <input
                                                                type="number"
                                                                value={fee.amount}
                                                                onChange={(e) => updateFeeRow(activeCurriculum, classIndex, feeIndex, 'amount', e.target.value)}
                                                                placeholder="0"
                                                                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-2 text-center">
                                                            <button
                                                                onClick={() => removeFeeRow(activeCurriculum, classIndex, feeIndex)}
                                                                className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Empty State */}
                                {classItem.isExpanded && classItem.fees.length === 0 && (
                                    <div className="border-t border-gray-200 px-4 py-8 text-center">
                                        <p className="text-sm text-gray-500 mb-3">No fees configured for this class</p>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => addFeeRow(activeCurriculum, classIndex)}
                                            leftIcon={<Plus size={14} />}
                                        >
                                            Add First Fee
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeeStructure;
